import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, Scale, Shield } from 'lucide-react';
import API from '../utils/api';

/**
 * Public Terms of Service page. Pulls the current ToS from /api/tos/current
 * (no auth required) and renders all sections in a long-form layout suitable
 * for sharing as a direct link (e.g., from the landing footer).
 */
export default function Terms() {
  var [data, setData] = useState(null);
  var [error, setError] = useState(null);

  useEffect(function () {
    axios.get(API + '/tos/current')
      .then(function (r) { setData(r.data); })
      .catch(function () { setError('Unable to load Terms of Service. Please try again later.'); });
  }, []);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#F6F7FC]" data-testid="terms-page">
      {/* Header */}
      <header className="border-b border-[#DDE1EE] bg-white/80 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-[#3A3566] hover:text-[#2D2560] transition-colors" data-testid="terms-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <img src="/parentum-logo.png" alt="Parentum" className="h-6 w-auto max-w-full parentum-logo-purple-mid" />
        </div>
      </header>

      {/* Title */}
      <section className="max-w-4xl mx-auto px-6 pt-10 pb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EEEFF8] border border-[#DDE1EE] mb-4">
          <Scale className="w-4 h-4 text-[#2D2560]" />
          <span className="text-xs font-bold uppercase tracking-wider text-[#2D2560]">Legal</span>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1E1A3E] mb-3 leading-tight">Terms of Service</h1>
        <p className="text-sm text-[#3A3566] leading-relaxed max-w-2xl">
          By creating an account, accessing, or using the Parentum Platform, you agree to be legally bound by these Terms of Service and our Privacy Policy. Please read them carefully.
        </p>
        {data && (
          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-[#6B6690]">
            <span className="px-2.5 py-1 rounded-md bg-white border border-[#DDE1EE]" data-testid="terms-version">Version {data.version}</span>
            {data.effective_date && <span>Effective {data.effective_date}</span>}
            {data.last_updated && <span>· Last updated {data.last_updated}</span>}
          </div>
        )}
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#10B981]/10 border border-[#10B981]/25 mb-8" data-testid="terms-non-sale-badge">
          <Shield className="w-4 h-4 text-[#10B981]" />
          <span className="text-xs text-[#0F6A4A] font-medium">Parentum does not sell, rent, or trade your personal data.</span>
        </div>

        {error && (
          <div className="rounded-xl border border-[#EF4444]/25 bg-[#EF4444]/5 p-4 text-sm text-[#EF4444]" data-testid="terms-error">{error}</div>
        )}

        {!error && !data && (
          <div className="space-y-3" data-testid="terms-loading">
            {[0, 1, 2, 3, 4].map(function (i) {
              return <div key={i} className="h-20 rounded-xl bg-white border border-[#DDE1EE] animate-pulse" />;
            })}
          </div>
        )}

        {data && Array.isArray(data.sections) && (
          <div className="space-y-4">
            {data.sections.map(function (s) {
              return (
                <article
                  key={s.id}
                  id={s.id}
                  className={
                    'rounded-2xl bg-white border p-6 transition-shadow hover:shadow-md ' +
                    (s.highlight ? 'border-[#F59E0B]/40' : 'border-[#DDE1EE]')
                  }
                  data-testid={'terms-section-' + s.id}
                >
                  <div className="flex items-start gap-3 mb-3">
                    {s.highlight ? (
                      <AlertTriangle className="w-5 h-5 text-[#F59E0B] mt-0.5 shrink-0" />
                    ) : (
                      <Scale className="w-5 h-5 text-[#5A4E9E] mt-0.5 shrink-0" />
                    )}
                    <h2 className="font-serif-display text-xl sm:text-2xl text-[#1E1A3E] leading-snug">{s.title}</h2>
                  </div>
                  {s.highlight && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/25 mb-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#B45309]">Critical Disclaimer</span>
                    </div>
                  )}
                  <p className="text-sm text-[#3A3566] leading-relaxed whitespace-pre-line">{s.content}</p>
                </article>
              );
            })}
          </div>
        )}

        <p className="mt-10 text-xs text-[#6B6690] text-center">
          For questions about these Terms, contact <a href="mailto:Support@parentum.app" className="text-[#2D2560] underline hover:text-[#5A4E9E]">Support@parentum.app</a>.
        </p>
      </section>

      <footer className="border-t border-[#DDE1EE] py-6 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-[#8C86C4]">&copy; 2026 Parentum. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
